import React, { Component } from 'react'
import { RasaContext } from 'context'
import { HeaderComponent } from 'components/header/component'
import { RasaDataGrid, PagingSource } from '../../generic/rasaDataGrid'
import { Loading } from 'components/loading'
import * as Flash from '../../components/flash'
import * as Columns from '../../components/grid/columns'
import { DateTimeCell } from 'components/gridcells/datetime'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { BlastsAPI } from './blasts-api'
import './styles.scss'

interface HistoryBlastsState {
  communityId: string,
  isLoading: boolean,
  panelWidth: number,
  blasts: any[],
  isLoadingBlasts: boolean,
  accountGuid: string,
  identityCommunityGuid: string,
  hasBlastsAccess: boolean,
}

export class HistoryBlastsComponent extends Component<any, HistoryBlastsState> {
  public static contextType = RasaContext

  constructor(props: any) {
    super(props)
    this.state = {
      communityId: '',
      isLoading: true,
      panelWidth: 0,
      blasts: [],
      isLoadingBlasts: false,
      accountGuid: '',
      identityCommunityGuid: '',
      hasBlastsAccess: false,
    }
  }

  public componentDidMount() {
    window.addEventListener('resize', this.handleResize)
    this.initialize()
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  private handleResize = () => {
    this.setState({
      panelWidth: window.innerWidth - Columns.LEFT_PANEL_DEFAULT_WIDTH,
    })
  }

  private initialize = () => {
    this.context.user.init().then(({ person, activeCommunity }) => {
      const accountGuid = (activeCommunity.data || {}).community_account_guid
      const identityCommunityGuid = activeCommunity.data.identity_community_guid
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
      const hasBlastsAccess = avlFeatures.indexOf(BillingPlanDetailCode.BLASTS) > -1

      this.setState({
        communityId: activeCommunity.communityId,
        isLoading: false,
        panelWidth: window.innerWidth - Columns.LEFT_PANEL_DEFAULT_WIDTH,
        accountGuid,
        identityCommunityGuid,
        hasBlastsAccess,
      }, () => {
        if (hasBlastsAccess) {
          this.fetchBlasts(activeCommunity.communityId, accountGuid, identityCommunityGuid)
        }
      })
    })
    .catch((error) => {
      this.setState({
        isLoading: false,
      }, () => {
        this.context.store.dispatch(Flash.showFlashError(error))
      })
    })
  }

  private fetchBlasts = (communityId: string, accountGuid: string, identityCommunityGuid: string) => {
    this.setState({ isLoadingBlasts: true })

    BlastsAPI.fetchListsAndBlasts(accountGuid)
      .then(([lists, blasts]) => {
        // Filter lists by newsletter_guid and source_type
        const newsletterList = lists.find((list: any) =>
          list.options && list.options.newsletter_guid === identityCommunityGuid &&
          list.options.source_type === 'newsletter'
        )

        if (!newsletterList) {
          this.setState({
            blasts: [],
            isLoadingBlasts: false,
          })
          return
        }

        // Filter blasts by the newsletter list ID and past send dates
        const now = new Date()
        const filteredBlasts = blasts.filter((blast: any) =>
          blast.lists && blast.lists.some((list: any) => list.id === newsletterList.id) &&
          blast.start_date && new Date(blast.start_date) < now
        )

        this.setState({
          blasts: filteredBlasts || [],
          isLoadingBlasts: false,
        })
      })
      .catch((error) => {
        this.setState({
          blasts: [],
          isLoadingBlasts: false,
        }, () => {
          this.context.store.dispatch(Flash.showFlashError('Failed to load blast history'))
        })
      })
  }

  private getColumns = (): Columns.SizableColumn[] => {
    return [
      {
        field: 'name',
        title: 'Name',
        width: 300,
        sortable: true,
      },
      {
        field: 'description',
        title: 'Description',
        width: 400,
        sortable: true,
      },
      {
        cell: DateTimeCell,
        field: 'start_date',
        title: 'Send Date',
        width: 300,
        sortable: true,
      },
    ]
  }

  public render() {
    if (this.state.isLoading) {
      return <Loading size="64" />
    }

    if (!this.state.hasBlastsAccess) {
      return (
        <div className="history-blasts-container">
          <div className="header-container">
            <HeaderComponent
              title={'BLASTS'}
              subTitle={'History'}
              description={['Blast campaigns are not available on your current plan. Please upgrade to access this feature.']}
            />
          </div>
        </div>
      )
    }

    return (
      <div className="history-blasts-container">
        <div className="header-container">
          <HeaderComponent
            title={'BLASTS'}
            subTitle={'History'}
            description={['View your past blast campaigns']}
          />
        </div>
        {this.state.isLoadingBlasts ? (
          <div className="main-loading"><Loading size="64" /></div>
        ) : (
          <div className="blasts-grid-container">
            <RasaDataGrid
              entityName="blast"
              datasetName=""
              datasetParams={[]}
              editDefaultState={false}
              sortable={true}
              pageable={true}
              pageSize={20}
              pagingSource={PagingSource.local}
              data={this.state.blasts}
              onDataReceived={() => {}}
            >
              {new Columns.SizableColumns(this.getColumns()).buildColumns(this.state.panelWidth)}
            </RasaDataGrid>
          </div>
        )}
      </div>
    )
  }
}
